import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Pencil, Trash2 } from "lucide-react";
import Loader from "../layout/Loader";
import AdminProfile from "../user/AdminProfile";
import {
  useDeleteOrderMutation,
  useGetAdminOrdersQuery,
} from "../../redux/api/orderApi";
import { Helmet } from "react-helmet-async";

const ListOrders = () => {
  const { data, isLoading, error } = useGetAdminOrdersQuery();

  const [
    deleteOrder,
    { isLoading: isDeleteLoading, error: deleteError, isSuccess },
  ] = useDeleteOrderMutation();

  useEffect(() => {
    if (error) {
      toast.error(error?.data?.message);
    }

    if (deleteError) {
      toast.error(deleteError?.data?.message);
    }

    if (isSuccess) {
      toast.success("Order Deleted");
    }
  }, [error, deleteError, isSuccess]);

  const deleteOrderHandler = (id) => {
    deleteOrder(id);
  };

  if (isLoading) return <Loader />;

  return (
    <AdminProfile>
      <Helmet>
        <title>Orders</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-6xl mx-auto mt-10 ">
        <h1 className="text-2xl font-playfair text-[#1c1917] mb-6">
          {data?.orders?.length} Orders
        </h1>

        {/* Responsive wrapper */}
        <div className="bg-[#f5f3ea] rounded-lg shadow border border-[#e2e1d8] overflow-x-auto">
          <table className="w-full min-w-[200px] table-auto">
            <thead className="bg-white/70">
              <tr className="text-left text-[#1c1917] font-playfair text-xs sm:text-sm uppercase tracking-wide border-b border-[#e2e1d8]">
                <th className="px-2 sm:px-2 py-3">ID</th>
                <th className="px-2 sm:px-2 py-3">Payment Status</th>
                <th className="px-1 sm:px-2 py-3">Order Status</th>
                <th className="px-1 sm:px-2 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y w-full text-center divide-[#e2e1d8] font-sourcesans text-xs sm:text-sm">
              {data?.orders?.map((order) => (
                <tr key={order?._id} className="hover:bg-white/50 transition">
                  <td className="px-2 sm:px-2 break-all">{order?._id}</td>
                  <td
                    className={`px-1 sm:px-2 py-3 font-medium ${
                      order?.paymentInfo?.status === "Paid"
                        ? "text-green-600"
                        : "text-stone-800"
                    }`}
                  >
                    {order?.paymentInfo?.status?.toUpperCase()}
                  </td>
                  <td
                    className={`px-1 sm:px-2 py-3 font-medium ${
                      order?.orderStatus?.toLowerCase() === "delivered"
                        ? "text-green-600"
                        : order?.orderStatus?.toLowerCase() === "processing"
                        ? "text-red-600"
                        : order?.orderStatus?.toLowerCase() === "shipped"
                        ? "text-orange-500"
                        : "text-stone-700"
                    }`}
                  >
                    {order?.orderStatus}
                  </td>
                  <td className="px-2 sm:px-2 py-6 text-center">
                    <div className="flex  justify-end flex-wrap gap-2">
                      <Link
                        to={`/admin/orders/${order?._id}`}
                        className="px-2 py-1 border border-[#8a4b32]/50 rounded-md text-xs text-[#1c1917] hover:border-[#BC3F4A] hover:text-[#BC3F4A] transition"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Link>

                      <button
                        onClick={() => deleteOrderHandler(order?._id)}
                        disabled={isDeleteLoading}
                        className="px-2 py-1 border border-[#BC3F4A]/60 rounded-md text-xs text-[#BC3F4A] hover:bg-[#BC3F4A] hover:text-white transition disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {data?.orders?.length === 0 && (
                <tr>
                  <td
                    colSpan="4"
                    className="px-4 py-6 text-center text-[#1c1917] font-sourcesans"
                  >
                    No orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminProfile>
  );
};

export default ListOrders;
